import OpenAI from 'openai';

/**
 * OpenAI service for analyzing call buyer KPIs and providing bidding recommendations
 */
class OpenAIService {
  constructor() {
    this.openai = new OpenAI({
      apiKey: import.meta.env.VITE_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true, // Required for client-side usage in React
    });
    
    // Track quota status to prevent repeated failures
    this.quotaExceeded = false;
    this.lastQuotaCheck = null;
  }

  /**
   * Check if we should attempt API calls based on previous quota status
   */
  shouldAttemptApiCall() {
    // Reset quota status after 1 hour
    if (this.lastQuotaCheck && Date.now() - this.lastQuotaCheck > 3600000) {
      this.quotaExceeded = false;
      this.lastQuotaCheck = null;
    }
    
    return !this.quotaExceeded;
  }

  /**
   * Handle OpenAI API errors with specific messaging
   */
  handleApiError(error) {
    console.error('OpenAI API Error:', error);
    
    if (error?.status === 429) {
      this.quotaExceeded = true;
      this.lastQuotaCheck = Date.now();
      throw new Error('OpenAI API quota exceeded. Please check your billing plan or try again later.');
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your configuration.');
    } else if (error?.status === 503) {
      throw new Error('OpenAI service is temporarily unavailable. Please try again later.');
    } else if (error?.code === 'network_error' || !navigator.onLine) {
      throw new Error('Network connection issue. Please check your internet connection.');
    } else {
      throw new Error(`API Error: ${error?.message || 'Unknown error occurred'}`);
    }
  }

  /**
   * Generate fallback analysis when API is unavailable
   */
  generateFallbackAnalysis(kpiData) {
    const avgCPA = kpiData?.avgCPA || 0;
    const winRate = kpiData?.winRate || 0;
    const qualityScore = kpiData?.qualityScore || 0;
    
    // Basic analysis based on thresholds
    let overallAssessment = 'Performance analysis unavailable due to API limitations. ';
    if (avgCPA > 50) {
      overallAssessment += 'CPA appears high - consider optimizing bidding strategy.';
    } else if (winRate < 30) {
      overallAssessment += 'Win rate is below optimal - review targeting criteria.';
    } else {
      overallAssessment += 'Performance metrics are within expected ranges.';
    }

    const topStates = kpiData?.statePerformance?.slice(0, 3)?.map(state => ({
      state: state?.state || 'N/A',
      reason: `Win rate: ${state?.winRate || 0}%, CPA: $${state?.cpa || 0}`,
      recommendedBidAdjustment: state?.winRate > 35 ? '+10%' : state?.winRate < 25 ? '-5%' : 'Maintain'
    })) || [
      { state: 'CA', reason: 'Historically strong performance', recommendedBidAdjustment: '+10%' },
      { state: 'TX', reason: 'Good conversion rates', recommendedBidAdjustment: '+5%' },
      { state: 'FL', reason: 'Stable performance', recommendedBidAdjustment: 'Maintain' }
    ];

    return {
      overallAssessment,
      topStates,
      cpaOptimization: [
        'Review high-cost states and adjust bid multipliers',
        'Focus on lead types with better conversion rates',
        'Monitor call quality metrics for optimization opportunities'
      ],
      callQualityInsights: [
        'Average call length indicates good lead engagement',
        'Monitor call completion rates across different regions',
        'Consider time-of-day bidding adjustments'
      ],
      biddingStrategyAdjustments: [
        'Implement geographic bid multipliers based on performance',
        'Adjust bids during peak performance hours',
        'Set CPA targets per lead type for better control'
      ],
      confidenceScore: 0.6, // Lower confidence for fallback data
      isFallback: true
    };
  }

  /**
   * Generate fallback quick insights when API is unavailable
   */
  generateFallbackInsights(realtimeData) {
    const winRate = realtimeData?.winRate || 0;
    const avgCPA = realtimeData?.avgCPA || 0;
    const qualityScore = realtimeData?.qualityScore || 0;

    const insights = [];
    
    if (winRate < 25) {
      insights?.push({
        title: 'Low Win Rate Alert',
        description: 'Win rate below 25%. Consider reviewing bid amounts or targeting criteria.',
        priority: 'high'
      });
    } else if (winRate > 40) {
      insights?.push({
        title: 'Strong Performance',
        description: 'Excellent win rate. Monitor for opportunities to increase bid volume.',
        priority: 'low'
      });
    }

    if (avgCPA > 60) {
      insights?.push({
        title: 'High CPA Warning',
        description: 'Cost per acquisition is elevated. Review bidding strategy.',
        priority: 'high'
      });
    } else if (avgCPA < 30) {
      insights?.push({
        title: 'Efficient CPA',
        description: 'CPA is performing well. Consider scaling successful campaigns.',
        priority: 'low'
      });
    }

    if (qualityScore < 7) {
      insights?.push({
        title: 'Quality Score Alert',
        description: 'Quality score needs improvement. Focus on lead relevance.',
        priority: 'medium'
      });
    }

    // Ensure we have at least one insight
    if (insights?.length === 0) {
      insights?.push({
        title: 'Stable Performance',
        description: 'Performance metrics are within normal ranges. Continue monitoring.',
        priority: 'low'
      });
    }

    return {
      insights: insights?.slice(0, 3), // Limit to 3 insights
      overallStatus: winRate > 35 ? 'Performing Well' : winRate > 25 ? 'Needs Attention' : 'Requires Review',
      isFallback: true
    };
  }

  /**
   * Analyzes KPI data to provide bidding recommendations
   * @param {Object} kpiData - KPI data including CPA, call length, win rate, etc.
   * @returns {Promise<Object>} AI analysis with recommendations
   */
  async analyzeKPIData(kpiData) {
    try {
      // Check if we should attempt API call
      if (!this.shouldAttemptApiCall()) {
        console.warn('Skipping OpenAI API call due to previous quota exceeded error');
        return this.generateFallbackAnalysis(kpiData);
      }

      const response = await this.openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are an expert insurance lead bidding strategist. Analyze KPI data and provide actionable recommendations for optimizing bidding strategies. Focus on cost per acquisition (CPA), average call length, win rates, and geographic performance. Provide specific, data-driven recommendations.`
          },
          {
            role: 'user',
            content: `Analyze this insurance lead bidding KPI data and provide recommendations:
            
            Current Performance:
            - Cost Per Acquisition (CPA): $${kpiData?.avgCPA || 'N/A'}
            - Average Call Length: ${kpiData?.avgCallLength || 'N/A'} minutes
            - Win Rate: ${kpiData?.winRate || 'N/A'}%
            - Total Bids: ${kpiData?.totalBids || 'N/A'}
            - Won Bids: ${kpiData?.wonBids || 'N/A'}
            - Total Spent: $${kpiData?.totalSpent || 'N/A'}
            - Quality Score: ${kpiData?.qualityScore || 'N/A'}/10
            
            Recent State Performance:
            ${kpiData?.statePerformance?.map(state => 
              `- ${state?.state}: Win Rate ${state?.winRate}%, CPA $${state?.cpa}, Avg Call ${state?.avgCallLength}min`
            )?.join('\n') || 'No state data available'}
            
            Lead Types:
            ${kpiData?.leadTypes?.map(type => 
              `- ${type?.type}: ${type?.count} leads, $${type?.avgCPA} CPA`
            )?.join('\n') || 'No lead type data available'}
            
            Please provide:
            1. Overall performance assessment
            2. Top 3 states to focus bidding on and why
            3. CPA optimization recommendations
            4. Call quality insights
            5. Bidding strategy adjustments`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'kpi_analysis_response',
            schema: {
              type: 'object',
              properties: {
                overallAssessment: { type: 'string' },
                topStates: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      state: { type: 'string' },
                      reason: { type: 'string' },
                      recommendedBidAdjustment: { type: 'string' }
                    },
                    required: ['state', 'reason', 'recommendedBidAdjustment']
                  }
                },
                cpaOptimization: {
                  type: 'array',
                  items: { type: 'string' }
                },
                callQualityInsights: {
                  type: 'array',
                  items: { type: 'string' }
                },
                biddingStrategyAdjustments: {
                  type: 'array',
                  items: { type: 'string' }
                },
                confidenceScore: { type: 'number' }
              },
              required: ['overallAssessment', 'topStates', 'cpaOptimization', 'callQualityInsights', 'biddingStrategyAdjustments', 'confidenceScore'],
              additionalProperties: false
            }
          }
        },
        temperature: 0.7,
        max_tokens: 2000,
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      result.isFallback = false;
      return result;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  /**
   * Generates quick insights for real-time KPI monitoring
   * @param {Object} realtimeData - Current real-time performance data
   * @returns {Promise<Object>} Quick AI insights
   */
  async generateQuickInsights(realtimeData) {
    try {
      // Check if we should attempt API call
      if (!this.shouldAttemptApiCall()) {
        console.warn('Skipping OpenAI API call due to previous quota exceeded error');
        return this.generateFallbackInsights(realtimeData);
      }

      const response = await this.openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: 'You are a real-time bidding analyst. Provide concise, actionable insights for current performance metrics. Keep responses brief and focused.'
          },
          {
            role: 'user',
            content: `Current performance snapshot:
            - Win Rate: ${realtimeData?.winRate || 'N/A'}%
            - CPA: $${realtimeData?.avgCPA || 'N/A'}
            - Quality Score: ${realtimeData?.qualityScore || 'N/A'}/10
            - Active Bids: ${realtimeData?.activeBids || 'N/A'}
            
            Provide 2-3 quick actionable insights for immediate optimization.`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'quick_insights_response',
            schema: {
              type: 'object',
              properties: {
                insights: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      priority: { type: 'string' }
                    },
                    required: ['title', 'description', 'priority']
                  }
                },
                overallStatus: { type: 'string' }
              },
              required: ['insights', 'overallStatus'],
              additionalProperties: false
            }
          }
        },
        temperature: 0.5,
        max_tokens: 800,
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      result.isFallback = false;
      return result;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  /**
   * Reset quota status (can be called manually if user wants to retry)
   */
  resetQuotaStatus() {
    this.quotaExceeded = false;
    this.lastQuotaCheck = null;
  }

  /**
   * Get current quota status
   */
  getQuotaStatus() {
    return {
      quotaExceeded: this.quotaExceeded,
      lastQuotaCheck: this.lastQuotaCheck,
      canRetry: !this.quotaExceeded || (this.lastQuotaCheck && Date.now() - this.lastQuotaCheck > 3600000)
    };
  }
}

export default new OpenAIService();